/** @jsx jsx */

import { Fragment, useState } from 'react';
import { jsx } from '@emotion/core';
import { v4 as uuidv4 } from 'uuid';

import {
  Alert,
  AlertDescription,
  ButtonGroup,
  CopyTextInput,
  Margin,
  Spacer,
  Surface,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableTd,
  TableTh,
  Text,
} from '@zapier/design-system';

import ConfirmationButton from 'app/developer-v3/components/ConfirmationButton';
import Link from 'app/common/components/Link';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import { ALL_VERSIONS_TEXT, BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { getAppUrl } from 'app/developer-v3/utils';
import { useAvroEvent } from 'app/developer-v3/hooks';

import SharingForm from './SharingForm';

import type { SemVer } from 'app/developer-v3/types/version';

type Invitee = {
  app_version: SemVer;
  email: string;
  customuser_id: string;
  id: string;
  role: string;
  status: string;
};

type Props = {
  appId: number;
  invitees: Array<Invitee>;
  fieldErrors: { email: string };
  inviteUrl: string;
  isSaving: boolean;
  onAddInvitee: (appId: number, email: string, appVersion: SemVer) => void;
  onDeleteInvitee: (appId: number, invitee: Invitee) => void;
};

export default function SharingPage({
  appId,
  fieldErrors,
  inviteUrl,
  invitees,
  isSaving,
  onAddInvitee,
  onDeleteInvitee,
}: Props) {
  const [inviteeIdDeleting, setInviteeIdDeleting] = useState(undefined);
  const [email, setEmail] = useState<string>('');
  const [appVersion, setAppVersion] = useState<SemVer>(ALL_VERSIONS_TEXT);

  const { emitPartnerUserInteractionEvent } = useAvroEvent();

  const onAdd = () => {
    onAddInvitee(appId, email, appVersion);
    const eventId = uuidv4();
    emitPartnerUserInteractionEvent({
      causationId: eventId,
      eventAction: 'click',
      eventId,
      interactionGoal: 'Share integration with user',
      interactionName: 'share_integration',
      interactionRawText: 'Invite',
    });
  };

  const onDelete = (inviteeId: string) => {
    const invitee = invitees.find(({ id }) => id === inviteeId);
    if (invitee) {
      onDeleteInvitee(appId, invitee);
    }

    const eventId = uuidv4();
    const causationId = eventId;
    const correlationId = uuidv4();
    const hasAccepted = `${inviteeId}`.startsWith('a');
    emitPartnerUserInteractionEvent({
      causationId,
      correlationId,
      eventAction: 'click',
      eventId,
      interactionGoal: 'Stop sharing integration with user',
      interactionName: 'stop_sharing_integration',
      interactionRawText: 'Delete',
      targetAccessLevel: 'user',
      targetCustomuserId: hasAccepted ? Number(invitee.customuser_id) : null,
      targetEmailAddress: invitee.email,
    });
  };

  return (
    <ManagerLayout
      description="Invite your users to start using your app as soon as you’re ready. They can be invited individually by email or you can broadly share your link."
      shouldLimitWidth={false}
      title="Sharing"
    >
      <Margin margin="40px 0">
        <Text tag="p" type="Body4">
          Invite your users to use your app with a link
        </Text>
        <Text margin="20px 0px" tag="p">
          You can allow anyone to use your app by having them join with this
          link. An integration admin is shown to be the inviter on the linked
          page. You can choose the inviter by making another admin the marketing
          contact on the{' '}
          <Link href={`${getAppUrl(appId)}/team`}>Manage team</Link> page.{' '}
          <Link
            href={`${BASE_DOC_URL_VB}/manage/add-team#assign-marketing-and-technical-contacts`}
            target="_blank"
          >
            Learn more about assigning marketing and technical contacts.
          </Link>
        </Text>
        <CopyTextInput aria-label="Invite URL" value={inviteUrl} />
      </Margin>
      <Margin margin="40px 0">
        <Text tag="p" type="Body4">
          Invite users by email and manage their access
        </Text>
        <Text margin="20px 0px" tag="p">
          Invite a user by their email address and choose which version(s) you
          want them to have access to.
        </Text>
        <SharingForm
          buttonLabel={isSaving ? 'Inviting...' : 'Invite'}
          error={fieldErrors?.email}
          integrationId={appId}
          onButtonClick={onAdd}
          onChangeVersion={(versionNext: SemVer) => setAppVersion(versionNext)}
          onUpdateField={(value: string) => {
            setEmail(value);
          }}
          version={appVersion}
        />
      </Margin>
      <Margin margin="40px 0 40px 0">
        <Surface borderColor="GrayWarm4">
          <Text margin="20px" tag="p">
            {invitees.length ? (
              <p>
                The below list of email addresses is <strong>not</strong> a
                comprehensive list of everyone who has accepted your invitation.
                It only includes those who accepted an invite via the automated
                email that’s generated from the above form.
              </p>
            ) : (
              <p>
                There aren’t any invitees yet, invite users by email to see them
                listed here!{' '}
              </p>
            )}
          </Text>
        </Surface>
        {!!invitees.length && (
          <Fragment>
            <Spacer height={40} />
            {invitees.length === 0 ? (
              <Alert>
                <AlertDescription>There aren't any invitees.</AlertDescription>
              </Alert>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableTh>Email</TableTh>
                    <TableTh>Version</TableTh>
                    <TableTh>Status</TableTh>
                    <TableTh>Actions</TableTh>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitees.map(invitee => (
                    <TableRow key={invitee.id}>
                      <TableTd>
                        <Link href={`mailto:${invitee.email}`}>
                          {invitee.email}
                        </Link>
                      </TableTd>
                      <TableTd>
                        {invitee.app_version || ALL_VERSIONS_TEXT}
                      </TableTd>
                      <TableTd>{invitee.status}</TableTd>
                      <TableTd>
                        <ButtonGroup>
                          <ConfirmationButton
                            actionText="Delete"
                            confirmationText="Really?"
                            isConfirming={inviteeIdDeleting === invitee.id}
                            onCancel={() => setInviteeIdDeleting(undefined)}
                            onClick={() => setInviteeIdDeleting(invitee.id)}
                            onConfirm={() => {
                              setInviteeIdDeleting(undefined);
                              onDelete(invitee.id);
                            }}
                          />
                        </ButtonGroup>
                      </TableTd>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Fragment>
        )}
      </Margin>
    </ManagerLayout>
  );
}
